import { assert } from '@ember/debug';
import Service, { inject as service } from '@ember/service';
import UnsavedChangesModalOld, { UnsavedChangesModalChoice } from '@dashboard/junk-drawer/components/unsaved-changes-modal-old';
function isChildRoute(child, potentialParent) {
  if (potentialParent === 'application') {
    // All other routes are children of the application route
    return child !== 'application';
  }
  return child.startsWith(`${potentialParent}.`);
}

/*

## Unsaved Changes Handler Service:

A handler for prompting the user with an unsaved changes modal when someone tries to transition
away from a page where they have unsaved changes.

To use this, register it when you insert your component or enter your route. It needs three callback functions.
hasUnsavedChanges should return a boolean for whether the user has made unsaved changes, which would be lost if a transition was
made to leave. If this is true, the unsaved changes modal will appear, prompting the user to either save or discard their changes.

If they choose to save their changes, the save callback is called. This should attempt to save the unsaved changes, and return a boolean
representing whether the transition should be retried (generally this should be whether the save was successful). If it returns true,
there should be no more unsaved changes, and the transition will be retried.

If they choose to discard their changes, the changes should be reverted as necessary. This function should return `true` to indicate that
the transition should be retried (if something went horribly wrong to cause the changes to not be discardable, you can return `false`).

If they close the unsaved changes modal otherwise (clicking the X, ESC, clicking the veil), none of the callbacks will be triggered and
the transition will not be retried.

The app/router is in charge of calling these methods when appropriate in `willTransition`. You can also manually trigger the unsaved
changes modal yourself when the user is not transitioning by calling `showPromptForRoute(routeName)` with the registered route name.

getBodyText, getSaveButtonVariant and getSaveButtonText are optional functions that be provided to customize the modal.
If getBodyText is provided, it will be called upon opening the modal to override the body text.
If getSaveButtonVariant is provided, it will be called upon opening the modal to override the `save` button variant.
If getSaveButtonText is provided, it will be called upon opening the modal to override the `save` button text.
*/
// eslint-disable-next-line ember/no-classic-classes
export default Service.extend({
  router: service(),
  modal: service(),
  init(...args) {
    this._super(...args);
    this.set('routes', []);
  },
  // to be used by Deprecated Unsaved Changes Modal.
  // this flag will be flipped when old unsaved changes
  // modal has "dismissed changes", so that the "willTransition"
  // can be told to ignore the check.
  ignoreOldUnsavedChanges: false,
  register({
    routeName,
    hasUnsavedChanges,
    save,
    discard,
    useMarketUI = false,
    getTitleText = () => null,
    getBodyText = () => null,
    getSaveButtonVariant = () => {},
    getSaveButtonText = () => null,
    getDiscardButtonText = () => null
  }) {
    assert('Should include a route name', routeName);
    assert('Should include a hasUnsavedChanges callback', hasUnsavedChanges);
    assert('Should include a save callback', save);
    assert('Should include a discard callback', discard);
    if (this.routes.some(route => route.name === routeName)) {
      assert(`The route ${routeName} has already been registered`);
    }
    const newRoute = {
      name: routeName,
      hasUnsavedChanges,
      save,
      discard,
      useMarketUI,
      getTitleText,
      getBodyText,
      getSaveButtonVariant,
      getSaveButtonText,
      getDiscardButtonText
    };

    // Sort routes such that any child routes would come before their parent routes
    const newRoutes = [...this.routes, newRoute].sort((routeA, routeB) => routeB.name.length - routeA.name.length);
    this.set('routes', newRoutes);
  },
  unregister(routeName) {
    const newRoutes = this.routes.filter(({
      name
    }) => name !== routeName);
    assert('The route should have already been registered', newRoutes.length < this.routes.length);
    this.set('routes', newRoutes);
  },
  // If the transition does not cause us to leave a particular route,
  // then we shouldn't check that route for unsaved changes.
  hasUnsavedChanges(transition) {
    // There will not be a pivot route for the initial transition
    if (!transition.pivotHandler) {
      return false;
    }

    // fullRouteName supports engine routes since mounted route is not
    // included in routeName
    const pivotRouteName = transition.pivotHandler.routeName;
    const pivotFullRouteName = transition.pivotHandler.fullRouteName;
    return this.routes.filter(({
      name
    }) => {
      return isChildRoute(name, pivotRouteName) || isChildRoute(name, pivotFullRouteName);
    }).some(({
      hasUnsavedChanges
    }) => hasUnsavedChanges(transition));
  },
  async promptForTransition(transition) {
    const pivotRouteName = transition.pivotHandler.routeName;
    const pivotFullRouteName = transition.pivotHandler.fullRouteName;

    // If a registered route is not a child of the pivot route,
    // then this transition isn't going to cause us to leave that route,
    // so we do not prompt for its unsaved changes
    const route = this.routes.filter(({
      name
    }) => {
      return isChildRoute(name, pivotRouteName) || isChildRoute(name, pivotFullRouteName);
    }).find(({
      hasUnsavedChanges
    }) => hasUnsavedChanges(transition));
    assert('There should only be a prompt for unsaved changes if there are unsaved changes', route);
    if (!route) {
      return true;
    }

    // If we happen to be on a subroute, we want to transtion to the parent with the unsaved changes
    // so that the modal appears over the page with the unsaved changes
    await this.router.transitionTo(route.name).followRedirects();
    return this.showPromptForRoute(route.name, transition);
  },
  async showPromptForRoute(routeName, transition) {
    const route = this.routes.find(({
      name
    }) => name === routeName);
    if (!route) {
      assert(`showPromptForRoute requires a name that matches a registered route. You passed ${routeName}`);
    }
    const {
      save,
      discard,
      getTitleText,
      getBodyText,
      getSaveButtonVariant,
      getSaveButtonText,
      getDiscardButtonText,
      useMarketUI = false
    } = route;
    const choice = await this.modal.open(UnsavedChangesModalOld, {
      titleText: getTitleText(),
      bodyText: getBodyText(),
      saveButtonVariant: getSaveButtonVariant(),
      saveButtonText: getSaveButtonText(),
      discardButtonText: getDiscardButtonText(),
      useMarketUI
    });
    switch (choice) {
      case UnsavedChangesModalChoice.SAVE:
        return save(transition);
      case UnsavedChangesModalChoice.DISCARD:
        return discard(transition);
      case UnsavedChangesModalChoice.CANCEL:
        return false;
      default:
        return assert(`Unexpected choice from unsaved changes modal: ${choice}`);
    }
  }
});